import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import { useTranslation } from "react-i18next";

import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "../../../components/Button/Button";
import { ConfirmModal } from "../../../components/Modal/ConfirmModal";

import AttachmentApi from "../../../services/AttachmentApi";
import { useAuth } from "../../../context/AuthContext";
import FileUploader, { ExistingFile } from "../../../components/FileUploader/FileUploader";

interface Attachment extends ExistingFile {}

const FailureAttachmentsTab: React.FC = () => {
  const { failureId } = useParams<{
    failureId: string;
    fmeaId: string;
  }>();
  const { plantId } = useAuth();
  const { displayToast } = useToast();
  const { t } = useTranslation("form");

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachment | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const fetchAttachments = async (): Promise<void> => {
    if (!failureId) return;
    try {
      setLoading(true);
      const response = await AttachmentApi.listAttachments(failureId);
      setAttachments(response.data);
    } catch (err) {
      displayToast("error", t("Error"), t("errorloadMessage"), false, 3000);
      setError(t("errorloadMessage"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (failureId) {
      fetchAttachments();
    }
  }, [failureId]);

  const handleUpload = async (): Promise<void> => {
    if (!failureId || !plantId || newFiles.length === 0) return;

    setIsUploading(true);
    let hasError = false;

    for (const file of newFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await AttachmentApi.uploadAttachment(failureId, plantId, formData);
      } catch (err) {
        hasError = true;
        console.error("Erro ao enviar o arquivo:", file.name, err);
        displayToast(
          "error",
          t("Error"),
          `${t("uploadErrorFor")} ${file.name}`,
          false,
          4000,
        );
      }
    }

    if (!hasError) {
      displayToast("success", t("Success"), t("filesUploaded"), false, 3000);
      setNewFiles([]);
      fetchAttachments();
    }
    setIsUploading(false);
  };

  const handleDownload = async (att: Attachment): Promise<void> => {
    if (!failureId || !att.id) {
      displayToast("error", t("Error"), t("downloadError"), false, 3000);
      return;
    }
    try {
      const response = await AttachmentApi.downloadAttachment(
        failureId,
        att.id,
      );
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const contentDisposition = response.headers["content-disposition"];
      let filename = att.fileName;

      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match != null && match[1]) {
          filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      displayToast("error", t("Error"), t("downloadError"), false, 3000);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!selectedAttachment || !failureId) return;
    try {
      await AttachmentApi.deleteAttachment(failureId, selectedAttachment.id);
      displayToast("success", t("Success"), t("fileDeleted"), false, 3000);
      fetchAttachments();
    } catch (err) {
      displayToast("error", t("Error"), t("deleteError"), false, 3000);
    } finally {
      setShowDeleteDialog(false);
      setSelectedAttachment(null);
    }
  };

  return (
    <div className="flex flex-column gap-4">
      <div className="surface-card p-4 border-round shadow-1">
        <FileUploader
          files={newFiles}
          onFilesChange={setNewFiles}
          existingFiles={attachments}
          onDownload={handleDownload}
          onDeleteExisting={(file) => {
            setSelectedAttachment(file as Attachment);
            setShowDeleteDialog(true);
          }}
          labels={{
            title: t("attachments") || "Anexos",
            choose: t("choose") || "Escolher",
            dragAndDrop: t("dragAndDrop") || "Arraste e solte arquivos aqui",
            existingFilesSection: t("attachedFiles") || "Arquivos Anexados",
            noFiles: t("noAttachmentsFound") || "Nenhum anexo encontrado.",
          }}
        />

        {newFiles.length > 0 && (
          <div className="flex justify-content-end mt-4">
            <Button
              label={t("upload") || "Fazer Upload"}
              icon="upload"
              severity="success"
              onClick={handleUpload}
              loading={isUploading}
            />
          </div>
        )}
      </div>

      {loading && attachments.length === 0 && (
        <div className="flex justify-content-center p-4">
          <ProgressSpinner />
        </div>
      )}

      {error && <div className="text-red-500 p-4 text-center">{error}</div>}

      <ConfirmModal
        header={t("confirmExclusion")}
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onAccept={confirmDelete}
        message={`${t("areYouSureDelete")} ${selectedAttachment?.fileName}?`}
      />
    </div>
  );
};

export default FailureAttachmentsTab;
